import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import AuthService from "../services/AuthService";
import { Modal, Box, Typography, Button } from "@mui/material";
import Roles from "../config/roles";
import {useTranslation} from "react-i18next";

// Create Context
export const AuthContext = createContext();

// Inactivity settings
const INACTIVITY_LIMIT = 1800 * 1000; // Show warning after 30min of inactivity
const WARNING_TIMEOUT = 30; // Countdown 30 seconds before logout


export const AuthProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [authToken, setAuthToken] = useState(localStorage.getItem("JWT_TOKEN") || null);
    const [authState, setAuthState] = useState({
        isLoggedIn: localStorage.getItem("LOGGED_IN") === "true",
        user: null,
        authToken: authToken,
    });
    const [role, setRole] = useState(() => {
        const savedRole = localStorage.getItem("ROLE");
        return savedRole ? parseInt(savedRole, 10) : null;
    });
    const [hasActiveSubscription, setHasActiveSubscription] = useState(
        () => localStorage.getItem("HAS_SUBSCRIPTION") === "true"
    );


    // Inactivity tracking state
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [countdown, setCountdown] = useState(WARNING_TIMEOUT);

    const activityTimeout = useRef(null);
    const countdownInterval = useRef(null);

    useEffect(() => {
        const savedRole = localStorage.getItem("ROLE");
        if (savedRole) {
            setRole(parseInt(savedRole, 10));
        }
    }, []);


      useEffect(() => {
        if (authState.isLoggedIn) {
          resetInactivityTimer(); // (re)start inactivity tracking when logged in
        } else {
          clearTimers();
        }
      }, [authState.isLoggedIn]);

    const login = async (credentials) => {
        try {
            const userData = await AuthService.login(credentials);
            console.log(userData)
            localStorage.setItem("LOGGED_IN", "true");
            localStorage.setItem("JWT_TOKEN", userData.jwtToken);
            localStorage.setItem("ROLE",  Roles[userData.role] );
            i18n.changeLanguage(userData.languageCode.toLowerCase());

            setAuthToken(userData.jwtToken);
            setAuthState({ isLoggedIn: true, user: userData, authToken: userData.jwtToken });
            setRole(Roles[userData.role])

            resetInactivityTimer();
            return userData;
        } catch (error) {
            console.log(error)
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem("LOGGED_IN");
        localStorage.removeItem("JWT_TOKEN");
        localStorage.removeItem("ROLE");
        localStorage.removeItem("HAS_SUBSCRIPTION");
        setAuthToken(null);
        setHasActiveSubscription(false);
        setAuthState({ isLoggedIn: false, user: null, authToken: null });
        clearTimers();
        window.location.href = "/login";
    };

    const refreshToken = async () => {
        try {
            const response = await AuthService.refresh(authToken);
            const newToken = response.jwtToken;
            if (newToken) {
                setAuthToken(newToken);
                localStorage.setItem("JWT_TOKEN", newToken);
            }
        } catch (error) {
            logout();
        }
    };

    // // Reset inactivity timer
    const resetInactivityTimer = () => {
        clearTimers();
        setIsWarningOpen(false);

        activityTimeout.current = setTimeout(() => {
            showWarningModal();
        }, INACTIVITY_LIMIT);
    };

    // Show warning modal — countdown is driven by the useEffect below
    const showWarningModal = () => {
        setIsWarningOpen(true);
    };
    //
    // // Stay logged in and reset timers
    const stayLoggedIn = () => {
        clearTimers();
        setIsWarningOpen(false);
        resetInactivityTimer();
    };

    // Clear all timers
    const clearTimers = () => {
        if (activityTimeout.current) {
            clearTimeout(activityTimeout.current);
            activityTimeout.current = null;
        }
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }
    };

    // Listen for user activity
    useEffect(() => {
        const resetOnActivity = () => {
            if (!isWarningOpen) {
                resetInactivityTimer();
            }
        };

        const events = ["mousemove", "keydown", "scroll", "click"];
        events.forEach((event) => window.addEventListener(event, resetOnActivity));

        return () => {
            events.forEach((event) => window.removeEventListener(event, resetOnActivity));
            clearTimers();
        };
    }, [isWarningOpen, authState.isLoggedIn]);

   useEffect(() => {
     if (!isWarningOpen) return;

     setCountdown(WARNING_TIMEOUT); // reset to full seconds when opening

     const id = setInterval(() => {
       setCountdown(prev => {
         if (prev <= 1) {
           clearInterval(id);
           setIsWarningOpen(false);
           clearTimers();  // stop idle timer
           logout();       // perform logout
           return 0;
         }
         return prev - 1;
       });
     }, 1000);

     return () => clearInterval(id);
   }, [isWarningOpen]);

    return (
        <AuthContext.Provider value={{ authState, login, logout, setAuthState, role, refreshToken, hasActiveSubscription, setHasActiveSubscription }}>
            {children}

            {/* Inactivity Warning Modal */}
            <Modal open={isWarningOpen}>
                <Box sx={{
                    width: 400,
                    margin: "auto",
                    marginTop: "10%",
                    padding: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center"
                }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>
                        You will be logged out in {countdown} seconds due to inactivity.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: "#427898", color: "white" }}
                        onClick={stayLoggedIn}
                    >
                        Stay Logged In
                    </Button>
                </Box>
            </Modal>
        </AuthContext.Provider>
    );
};
